// <Barcode> — inline SVG barcode renderer over the pure encoders (EAN-13 /
// Code 128 / ISSN auto-selection via makeBarcode). Ported from shippra
// ui/Barcode.tsx: silently renders nothing when the value does not encode
// (makeBarcode → null), so a register can slap it under any code column.
import { Show, createMemo } from "solid-js";
import { makeBarcode } from "./barcode.js";
import type { Symbology } from "./barcode.js";

export function Barcode(props: {
  value: string;
  symbology?: Symbology;
  /** Bar height in px (default 28). */
  height?: number;
  /** Width of one module in px (default 1.6). */
  moduleWidth?: number;
  /** Alias for `moduleWidth` (module width in px); `moduleWidth` wins when both are given. Default 1.6. */
  width?: number;
  /** Bar + text color (any CSS color, e.g. a theme token via `currentColor`). Default "currentColor". */
  color?: string;
  /** Print the value under the bars. */
  showText?: boolean;
  class?: string;
}) {
  const data = createMemo(() => makeBarcode(props.value, props.symbology ?? "auto"));
  return (
    <Show when={data()}>
      {(d) => {
        const mw = (): number => props.moduleWidth ?? props.width ?? 1.6;
        const h = (): number => props.height ?? 28;
        const fill = (): string => props.color ?? "currentColor";
        const width = (): number => d().bits.length * mw();
        const textH = (): number => (props.showText ? 12 : 0);
        // merged runs — far fewer rects than per-module
        const runs = (): { x: number; w: number }[] => {
          const out: { x: number; w: number }[] = [];
          const bits = d().bits;
          let i = 0;
          while (i < bits.length) {
            if (bits[i] === 1) {
              let j = i;
              while (j < bits.length && bits[j] === 1) j++;
              out.push({ x: i * mw(), w: (j - i) * mw() });
              i = j;
            } else i++;
          }
          return out;
        };
        return (
          <svg
            class={props.class}
            width={width()}
            height={h() + textH()}
            viewBox={`0 0 ${width()} ${h() + textH()}`}
            role="img"
            aria-label={`Barcode ${props.value}`}
          >
            {runs().map((r) => (
              <rect x={r.x} y={0} width={r.w} height={h()} fill={fill()} />
            ))}
            <Show when={props.showText}>
              <text
                x={width() / 2}
                y={h() + 10}
                text-anchor="middle"
                font-size="9"
                font-family="ui-monospace, monospace"
                fill={fill()}
              >
                {props.value}
              </text>
            </Show>
          </svg>
        );
      }}
    </Show>
  );
}
